import React, { useCallback, useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { fetchTraderOffersData } from "../../../../Rest_api/DemoRestApi";
import loader from "../../../../components/recharts/loader.gif";
import { EditOfferDataModal } from "./EditOffer";
import { ReactComponent as Edit } from "../../../../icons/edit_light.svg";

interface TraderOffer {
  start_slot: string;
  end_slot: string;
  quantum: string;
  rate: string;
}

interface OfferRow {
  start_date: string;
  end_date: string;
  type: "RTC" | "PEAK" | "CUSTOM";
  id: number;
  trader_offer: TraderOffer[];
}

interface TimeSlot {
  id: string;
  start_slot: string;
  end_slot: string;
  quantum: string;
  rate: string;
  label?: string;
}

interface TransformedOffer {
  id: string;
  startDate: string;
  endDate: string;
  type: "RTC" | "PEAK" | "CUSTOM";
  timeSlots: TimeSlot[];
  originalId?: number;
}

const OfferListTable = ({ refreshKey }: { refreshKey: any }) => {
  const [offers, setOffers] = React.useState<OfferRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string>("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState<TransformedOffer | null>(
    null
  );
  const [expandedOfferId, setExpandedOfferId] = useState<number | null>(null);

  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetchTraderOffersData();
      setOffers(res);
    } catch (error) {
      console.error("Error fetching offer data:", error);
      setError("Failed to load offers. Please try again.");
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  const transformOfferForEdit = (offer: OfferRow): TransformedOffer => {
    return {
      id: offer.id.toString(),
      startDate: offer.start_date,
      endDate: offer.end_date,
      type: offer.type,
      originalId: offer.id,
      timeSlots: offer.trader_offer.map((traderOffer, index) => ({
        id: `slot-${offer.id}-${index}`,
        start_slot: traderOffer.start_slot,
        end_slot: traderOffer.end_slot,
        quantum: traderOffer.quantum.toString(),
        rate: traderOffer.rate.toString(),
        label:
          offer.type === "PEAK"
            ? index === 0
              ? "Morning Peak"
              : "Evening Peak"
            : undefined,
      })),
    };
  };

  const handleEditClick = (offer: OfferRow) => {
    const transformedOffer = transformOfferForEdit(offer);
    setEditingOffer(transformedOffer);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = () => {
    setIsEditModalOpen(false);
    setEditingOffer(null);
    handleFetchData();
  };

  const getSlotLabel = (index: number, type: string) => {
    if (type === "PEAK") {
      return index === 0 ? "Morning Peak" : "Evening Peak";
    }
    return `0${index + 1}`;
  };

  const toggleAccordion = (offerId: number) => {
    setExpandedOfferId((prev) => (prev === offerId ? null : offerId));
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "RTC":
        return "text-[#F1935C]";
      case "PEAK":
        return "text-[#34656D]";
      case "CUSTOM":
        return "text-[#819ACC]";
      default:
        return "text-gray-700";
    }
  };

  return (
    <>
      <div className="w-full overflow-auto max-h-[calc(100vh-600px)] px-1">
        <div className="space-y-0">
          {loading && offers.length === 0 ? (
            <div className="h-full w-full bg-gray-50 flex items-center justify-center rounded-xl p-12">
              <img src={loader} width={200} alt="loader" className="block" />
            </div>
          ) : error ? (
            <div className="text-center py-12 bg-red-50 border-2 border-red-200 rounded-xl">
              <div className="text-red-600 text-lg font-semibold">{error}</div>
            </div>
          ) : offers.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl">
              <h2 className="mb-2 text-xl font-bold text-[#34656D]">
                No offers added yet
              </h2>
              <p className="text-base text-[#34656D]">
                Click "Add Offers" to get started with your trading offers
              </p>
            </div>
          ) : (
            offers.map((offer, offerIndex) => (
              <div
                key={offer.id}
                className={` bg-white rounded-xl border-[#DFEAF2] border py-1 px-1 ${
                  offerIndex === 0 ? "border-t" : ""
                }`}
              >
                {/* Header Row */}
                <div
                  className="flex items-center px-4 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleAccordion(offer.id)}
                >
                  {/* Chevron Icon */}
                  <div className="w-8 flex items-center justify-center mr-4">
                    {expandedOfferId === offer.id ? (
                      <ChevronUp size={18} className="text-gray-500" />
                    ) : (
                      <ChevronDown size={18} className="text-gray-500" />
                    )}
                  </div>

                  {/* Offer Name */}
                  <div className="flex-1 text-sm text-[#8C8A96] font-normal">
                    Offer Name
                  </div>

                  {/* Offer Type */}
                  <div className={`flex-1 text-sm font-medium ${getTypeColor(offer.type)}`}>
                    {offer.type}
                  </div>

                  {/* Start Date */}
                  <div className="flex-1 text-sm text-[#525062]">
                    {offer.start_date}
                  </div>

                  {/* End Date */}
                  <div className="flex-1 text-sm text-[#525062]">
                    {offer.end_date}
                  </div>

                  {/* Edit Icon */}
                  <div className="w-8 flex items-center justify-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(offer);
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Edit Offer"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedOfferId === offer.id && (
                  <div className="bg-white animate-fade-in">
                    {/* Table Header */}
                    <div className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-[#F3F8FF]">
                      <div className="w-8 mr-4"></div>
                      <div className="flex-1 text-xs font-medium text-[#819ACC] text-center">
                        Period
                      </div>
                      <div className="flex-1 text-xs font-medium text-[#819ACC] text-center">
                        Start Time
                      </div>
                      <div className="flex-1 text-xs font-medium text-[#819ACC] text-center">
                        End Time
                      </div>
                      <div className="flex-1 text-xs font-medium text-[#819ACC] text-center">
                        Rate (₹)
                      </div>
                      <div className="flex-1 text-xs font-medium text-[#819ACC] text-center">
                        Quantum (MW)
                      </div>
                      <div className="w-8"></div>
                    </div>

                    {/* Data Rows */}
                    {offer.trader_offer.map((traderOffer, index) => (
                      <div
                        key={index}
                        className="w-full flex items-center px-4 py-3 bg-white"
                      >
                        <div className="w-8 mr-4"></div>
                        <div className="flex-1 text-sm text-[#8C8A96] text-center">
                          {getSlotLabel(index, offer.type)}
                        </div>
                        <div className="flex-1 text-sm text-[#8C8A96] text-center">
                          {traderOffer.start_slot}
                        </div>
                        <div className="flex-1 text-sm text-[#8C8A96] text-center">
                          {traderOffer.end_slot}
                        </div>
                        <div className="flex-1 text-sm text-[#8C8A96] text-center">
                          {traderOffer.rate}
                        </div>
                        <div className="flex-1 text-sm text-[#8C8A96] text-center">
                          {traderOffer.quantum}
                        </div>
                        <div className="w-8"></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && editingOffer && (
        <EditOfferDataModal
          isModalOpen={isEditModalOpen}
          setIsModalOpen={setIsEditModalOpen}
          offerData={editingOffer}
          onUpdate={handleEditSubmit}
        />
      )}

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 1000px;
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default OfferListTable;